import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  Alert,
  Snackbar,
  CircularProgress,
  MenuItem
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { EasyCheckButton } from '../../common/Controls';
import { mobileChequeService } from '../../../services/mobile/mobileChequeService';
import { getAmountStep, validateAmountPrecision } from '../../../utils/currencyUtils';

const MobileChequeForm = () => {
  const navigate = useNavigate();
  const [banks, setBanks] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      bankId: '',
      beneficiaryName: '',
      chequeNumber: '',
      amount: '',
      chequeDate: new Date(),
      remarks: ''
    }
  });

  const selectedBankId = watch('bankId');
  const selectedBank = banks.find((b) => (b.Id || b.id) === selectedBankId);
  const selectedCurrency = (selectedBank && (selectedBank.Currency || selectedBank.currency)) || 'AED';

  // Load banks and beneficiaries
  useEffect(() => {
    const fetchData = async () => {
      setDataLoading(true);
      try {
        const [banksResult, beneResult] = await Promise.all([
          mobileChequeService.getBanks(),
          mobileChequeService.getBeneficiaries()
        ]);

        if (banksResult.success) {
          const bankData = Array.isArray(banksResult.data) ? banksResult.data : [];
          setBanks(bankData);
        }
        if (beneResult.success) {
          const beneData = Array.isArray(beneResult.data) ? beneResult.data : [];
          setBeneficiaries(beneData);
        }
      } catch {
        // handled by service layer
      } finally {
        setDataLoading(false);
      }
    };

    fetchData();
  }, []);

  // Auto-populate cheque number when bank changes
  useEffect(() => {
    if (!selectedBankId) return;

    const fetchNextNumber = async () => {
      try {
        const result = await mobileChequeService.getNextChequeNumber(selectedBankId);
        if (result.success) {
          const nextNum = result.data?.nextChequeNumber || result.data?.NextChequeNumber || result.data;
          if (nextNum) {
            setValue('chequeNumber', String(nextNum));
          }
        }
      } catch {
        // keep existing value
      }
    };

    fetchNextNumber();
  }, [selectedBankId, setValue]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        bankId: data.bankId,
        beneficiaryName: typeof data.beneficiaryName === 'string'
          ? data.beneficiaryName
          : data.beneficiaryName?.BeneficiaryName || data.beneficiaryName?.beneficiaryName || '',
        chequeNumber: data.chequeNumber,
        chqAmount: parseFloat(data.amount),
        chqDate: data.chequeDate instanceof Date
          ? data.chequeDate.toISOString().split('T')[0]
          : data.chequeDate,
        remarks: data.remarks || ''
      };

      const result = await mobileChequeService.createCheque(payload);

      if (result.success) {
        const newChequeId = result.data?.Id || result.data?.id;
        if (newChequeId) {
          navigate(`/mobile/cheques/${newChequeId}`);
        } else {
          navigate('/mobile/cheques');
        }
      } else {
        setSnackbar({ open: true, message: result.message || 'Failed to create cheque.', severity: 'error' });
      }
    } catch {
      setSnackbar({ open: true, message: 'An error occurred.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (dataLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: '#2563eb' }} />
      </Box>
    );
  }

  const beneficiaryNames = beneficiaries.map(
    (b) => b.BeneficiaryName || b.beneficiaryName || ''
  ).filter(Boolean);

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#0f172a' }}>
        New Cheque
      </Typography>

      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        {/* Bank Account */}
        <Controller
          name="bankId"
          control={control}
          rules={{ required: 'Bank account is required' }}
          render={({ field }) => (
            <TextField
              {...field}
              select
              fullWidth
              label="Bank Account"
              error={!!errors.bankId}
              helperText={errors.bankId?.message}
              sx={{ mb: 2 }}
              InputProps={{ sx: { borderRadius: 2, height: 56 } }}
            >
              {banks.map((bank) => (
                <MenuItem
                  key={bank.Id || bank.id}
                  value={bank.Id || bank.id}
                >
                  {bank.AccountName || bank.accountName} - {bank.AccountNumber || bank.accountNumber}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        {/* Beneficiary */}
        <Controller
          name="beneficiaryName"
          control={control}
          rules={{ required: 'Beneficiary is required' }}
          render={({ field: { onChange, value, ...field } }) => (
            <Autocomplete
              {...field}
              freeSolo
              options={beneficiaryNames}
              value={value}
              onChange={(_, newValue) => onChange(newValue || '')}
              onInputChange={(_, newValue) => onChange(newValue || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Beneficiary"
                  error={!!errors.beneficiaryName}
                  helperText={errors.beneficiaryName?.message}
                  sx={{ mb: 2 }}
                  InputProps={{
                    ...params.InputProps,
                    sx: { borderRadius: 2, height: 56 }
                  }}
                />
              )}
            />
          )}
        />

        {/* Cheque Number */}
        <Controller
          name="chequeNumber"
          control={control}
          rules={{ required: 'Cheque number is required' }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Cheque Number"
              error={!!errors.chequeNumber}
              helperText={errors.chequeNumber?.message}
              sx={{ mb: 2 }}
              InputProps={{ sx: { borderRadius: 2, height: 56 } }}
            />
          )}
        />

        {/* Amount */}
        <Controller
          name="amount"
          control={control}
          rules={{
            required: 'Amount is required',
            validate: (v) => {
              if (!(parseFloat(v) > 0)) return 'Amount must be greater than 0';
              if (parseFloat(v) > 9999999999.99) return 'Amount too large';
              return validateAmountPrecision(v, selectedCurrency);
            }
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Amount"
              type="text"
              inputProps={{ inputMode: 'decimal', step: getAmountStep(selectedCurrency) }}
              error={!!errors.amount}
              helperText={errors.amount?.message}
              sx={{ mb: 2 }}
              InputProps={{ sx: { borderRadius: 2, height: 56 } }}
            />
          )}
        />

        {/* Cheque Date */}
        <Controller
          name="chequeDate"
          control={control}
          rules={{ required: 'Date is required' }}
          render={({ field }) => (
            <DatePicker
              {...field}
              label="Cheque Date"
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.chequeDate,
                  helperText: errors.chequeDate?.message,
                  sx: { mb: 2 },
                  InputProps: { sx: { borderRadius: 2, height: 56 } }
                }
              }}
            />
          )}
        />

        {/* Remarks */}
        <Controller
          name="remarks"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Remarks"
              multiline
              rows={2}
              sx={{ mb: 3 }}
              InputProps={{ sx: { borderRadius: 2 } }}
            />
          )}
        />

        <EasyCheckButton
          type="submit"
          fullWidth
          disabled={loading}
          sx={{
            py: 1.75,
            borderRadius: 2,
            backgroundColor: '#2563eb',
            '&:hover': { backgroundColor: '#1d4ed8' },
            fontWeight: 600,
            fontSize: '1rem'
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Cheque'}
        </EasyCheckButton>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 2 }}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MobileChequeForm;
